import { useState } from "react";
import { HelpCircle, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Link } from "react-router-dom";
import { getDoc } from "../lib/docsContent";

/**
 * <HelpBubble feature="auto_op" /> renders a small circular help button.
 * Click → side drawer with that feature's Why/How cards.
 * Zero prop burden — just the feature key matching /app/frontend/src/lib/docsContent.js.
 */
export default function HelpBubble({ feature, size = "sm", className = "" }) {
  const [open, setOpen] = useState(false);
  const doc = getDoc(feature);
  if (!doc) return null;

  const dim = size === "xs" ? "w-3.5 h-3.5" : "w-4 h-4";

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        data-testid={`help-${feature}`}
        aria-label={`Help: ${doc.title}`}
        className={`inline-flex items-center justify-center rounded-full text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors p-0.5 ${className}`}
      >
        <HelpCircle className={dim} />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[420px] sm:max-w-[420px] overflow-y-auto bg-white" data-testid={`help-sheet-${feature}`}>
          <SheetHeader className="pb-4 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-blue-600 font-mono">{doc.section}</div>
            <SheetTitle className="text-lg text-slate-900">{doc.title}</SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Why it matters</div>
              <p className="text-sm text-slate-700 leading-relaxed">{doc.why}</p>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">How to use it</div>
              <ol className="text-sm text-slate-700 leading-relaxed space-y-1.5 list-decimal pl-5">
                {doc.how.map((step, i) => (
                  <li key={`${feature}-${i}`}>{step}</li>
                ))}
              </ol>
            </div>

            <Link
              to="/docs"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium mt-2"
              data-testid="help-full-docs"
            >
              Open full docs <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
